import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import ErrorBoundary from './ErrorBoundary';
import { XMarkIcon, DocumentArrowUpIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { categoriesAPI } from '../services/api';
import { mapSameBankCategory } from '../features/transactions/unknownCategories';
import { formatCalendarDay, parseImportCalendarDay } from '../utils/localDate';
import { formatApiError } from '../utils/errorMessage';
import { parseMoneyInput } from '../utils/money';
import { importRowDirection, importRowType } from '../utils/importRowDirection';
import { categoriesForTypes } from '../features/transactions/transactionTypes';
import { isLiabilityAccount, guessChargeSign, liabilityRowDirection, liabilityRowType } from '../utils/liabilityImport';
import { findDuplicates, defaultDecisions, rowsToImport, markDuplicates, duplicateCounts } from '../features/transactions/duplicateReview';

const FILE_READ_ERROR = 'This file could not be read. Check the format and try again.';

const ImportTransactionsModal = ({ isOpen, onClose, onImport, accounts, categories }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({
    date: '',
    description: '',
    amount: '',
    type: '',
    category: '',
    payee: '',
    reference: ''
  });
  const [selectedAccount, setSelectedAccount] = useState('');
  const [previewData, setPreviewData] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [showWarningConfirm, setShowWarningConfirm] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [duplicateDecisions, setDuplicateDecisions] = useState({});
  const [duplicateBatch, setDuplicateBatch] = useState([]); // rows the duplicate indexes refer to
  // Card / credit line / loan imports: which sign means a charge, and whether
  // payments to the account are saved as transfers.
  const [chargeSign, setChargeSign] = useState('negative');
  const [chargeSignTouched, setChargeSignTouched] = useState(false);
  const [paymentsAsTransfers, setPaymentsAsTransfers] = useState(true);
  const [step, setStep] = useState(1); // 1: Upload, 2: Map, 3: Preview, 4: Duplicates, 5: Categories
  const [isProcessing, setIsProcessing] = useState(false); // Loading state for async operations
  const [mappingNotice, setMappingNotice] = useState('');
  
  // Category management state
  const [allCategories, setAllCategories] = useState([]);
  const [unmatchedCategories, setUnmatchedCategories] = useState([]); // Bank categories that don't match our categories
  const [categoryMappings, setCategoryMappings] = useState({}); // {transactionIndex: selectedCategoryId}
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryType, setNewCategoryType] = useState('expense');
  const [currentBankCategory, setCurrentBankCategory] = useState(''); // For prefilling add category modal

  // Load all categories on mount
  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen]);

  const selectedAccountRow = (accounts || []).find((a) => String(a.id) === String(selectedAccount));
  const liabilityImport = isLiabilityAccount(selectedAccountRow);

  // Preselect the charge sign from the file (the sign most rows use).
  useEffect(() => {
    if (!liabilityImport || chargeSignTouched || columnMapping.amount === '') return;
    setChargeSign(guessChargeSign(parsedData.map((row) => parseAmount(row[columnMapping.amount]))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liabilityImport, chargeSignTouched, columnMapping.amount, parsedData]);

  // Bank direction and type for one row (cards follow the confirmed charge sign).
  const rowDirection = (rawAmount, indicator) => (liabilityImport
    ? liabilityRowDirection(rawAmount, chargeSign, indicator)
    : importRowDirection(rawAmount, indicator));
  const rowType = (label, rawAmount, indicator) => (liabilityImport
    ? liabilityRowType(label, rowDirection(rawAmount, indicator), paymentsAsTransfers)
    : determineType(label, rawAmount));

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const loadCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setAllCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
    }
  };

  // Data transformation utilities
  const parseDate = (excelDate) => parseImportCalendarDay(excelDate, XLSX.SSF.parse_date_code);

  // Returns a signed decimal string with two places, or null when the cell is
  // empty or not a clear amount ("1.234,56" and "12.345" are refused, never guessed).
  const parseAmount = (excelAmount) => {
    if (excelAmount === null || excelAmount === undefined || excelAmount === '') return null;

    let text;
    if (typeof excelAmount === 'number') {
      // Spreadsheet cells arrive as floats (30.30 can be 30.299999999999997);
      // only a value within float noise of whole cents is accepted.
      if (!Number.isFinite(excelAmount)) return null;
      const cents = Math.round(excelAmount * 100);
      if (!Number.isSafeInteger(cents) || Math.abs(excelAmount * 100 - cents) > 0.001) return null;
      const digits = String(Math.abs(cents)).padStart(3, '0');
      text = `${cents < 0 ? '-' : ''}${digits.slice(0, -2)}.${digits.slice(-2)}`;
    } else {
      text = String(excelAmount).replace(/[$\s€£]/g, '');
      if (text.includes('(') && text.includes(')')) {
        text = '-' + text.replace(/[()]/g, '');
      }
    }

    const { value, error } = parseMoneyInput(text, { allowNegative: true });
    if (error || value === null) return null;
    const [whole, fraction = ''] = value.split('.');
    return `${whole}.${fraction.padEnd(2, '0')}`;
  };

  const cleanDescription = (desc) => {
    if (!desc) return null;
    return String(desc)
      .trim()
      .replace(/\s+/g, ' ')
      .substring(0, 255); // Limit length
  };

  // Type hint from a type/category label. Only transfer/refund labels are
  // kept; income vs expense always follows the bank direction (sign or
  // debit/credit column), never words like "payment".
  const determineType = (typeColumn, amount) => importRowType(typeColumn, importRowDirection(amount, typeColumn));

  // Helper function to get color classes for transaction types
  const getTypeColorClass = (type) => {
    switch (type) {
      case 'income':
        return 'bg-green-100 text-green-800';
      case 'expense':
        return 'bg-red-100 text-red-800';
      case 'refund':
        return 'bg-blue-100 text-blue-800';
      case 'transfer':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Exact type keywords only. "Other Income" / "Investment Income" are real
  // bank categories and must go through matching, not this shortcut.
  const isBankCategoryATransactionType = (categoryName) => {
    if (!categoryName) return false;
    const lower = String(categoryName).trim().toLowerCase();
    return lower === 'income'
      || lower === 'expense'
      || lower === 'refund'
      || lower === 'transfer'
      || lower === 'interest income';
  };

  // Match bank category to existing category
  const matchBankCategory = async (bankCategoryName, transactionType) => {
    if (!bankCategoryName || !bankCategoryName.trim()) return null;
    
    try {
      const response = await categoriesAPI.match(bankCategoryName, transactionType);
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        return response.data[0]; // Return best match
      }
      return null;
    } catch (error) {
      console.error('Error matching category:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      // Don't throw - just return null so import can continue
      return null;
    }
  };

  const autoDetectColumns = (headers) => {
    const mapping = {
      date: '',
      description: '',
      amount: '',
      type: '',
      category: '',
      payee: '',
      reference: ''
    };

    headers.forEach((header, index) => {
      const lower = String(header).toLowerCase();
      
      // Date detection
      if ((lower.includes('date') || lower.includes('posted') || lower === 'date') && !mapping.date) {
        mapping.date = index;
      }
      
      // Description detection
      if ((lower.includes('description') || lower.includes('memo') || lower.includes('detail') || lower.includes('narrative')) && !mapping.description) {
        mapping.description = index;
      }
      
      // Amount detection - prioritize "amount" over "debit" or "credit"
      if (lower === 'amount' && !mapping.amount) {
        mapping.amount = index;
      } else if ((lower.includes('amount') || lower.includes('debit') || lower.includes('credit')) && !mapping.amount) {
        mapping.amount = index;
      }
      
      // Type detection
      if ((lower.includes('type') || lower.includes('transaction type') || lower === 'type') && !mapping.type) {
        mapping.type = index;
      }
      
      // Category detection
      if ((lower.includes('category') || lower === 'category') && !mapping.category) {
        mapping.category = index;
      }
      
      // Payee detection
      if ((lower.includes('payee') || lower.includes('vendor') || lower.includes('merchant')) && !mapping.payee) {
        mapping.payee = index;
      }
      
      // Reference detection
      if ((lower.includes('reference') || lower.includes('check') || lower.includes('ref') || lower === 'ref #') && !mapping.reference) {
        mapping.reference = index;
      }
    });

    return mapping;
  };

  const MAPPING_STORAGE_KEY = 'elonest.importColumnMapping';

  const headerNameAt = (headerRow, index) => {
    if (index === '' || index === null || index === undefined) return '';
    const i = Number(index);
    if (!Number.isInteger(i) || i < 0 || i >= headerRow.length) return '';
    return String(headerRow[i] ?? '');
  };

  const loadSavedMappingNames = () => {
    try {
      const raw = localStorage.getItem(MAPPING_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (err) {
      return null;
    }
  };

  const applySavedMapping = (headerRow, detectedMapping) => {
    const saved = loadSavedMappingNames();
    if (!saved) return { mapping: detectedMapping, notice: '' };
    const mapping = { ...detectedMapping };
    let applied = false;
    Object.keys(mapping).forEach((field) => {
      const name = saved[field];
      if (!name) {
        mapping[field] = '';
        return;
      }
      const index = headerRow.findIndex((header) => String(header) === String(name));
      if (index >= 0) {
        mapping[field] = index;
        applied = true;
      }
    });
    if (!applied) return { mapping: detectedMapping, notice: '' };
    return { mapping, notice: 'Saved column mapping applied.' };
  };

  const saveColumnMapping = () => {
    const named = {};
    Object.keys(columnMapping).forEach((field) => {
      named[field] = headerNameAt(headers, columnMapping[field]);
    });
    localStorage.setItem(MAPPING_STORAGE_KEY, JSON.stringify(named));
    setMappingNotice('Column mapping saved for the next import.');
  };

  const resetColumnMapping = () => {
    localStorage.removeItem(MAPPING_STORAGE_KEY);
    setColumnMapping(autoDetectColumns(headers));
    setMappingNotice('Saved column mapping reset.');
  };

  const goBackOneStep = () => {
    if (step === 4) {
      setStep(unmatchedCategories.length > 0 ? 5 : 3);
      return;
    }
    if (step === 5) {
      setStep(3);
      return;
    }
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleFileUpload = (e) => {
    if (!selectedAccount) {
      alert('Please select an account first');
      return;
    }

    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    setFile(uploadedFile);
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        // cellDates: CSV ISO days become Date at UTC midnight instead of a
        // fractional Excel serial that parse_date_code reads one day early.
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '' });

        if (jsonData.length < 2) {
          alert('File must contain at least a header row and one data row');
          return;
        }

        const headerRow = jsonData[0];
        const dataRows = jsonData.slice(1).filter(row => row.some(cell => cell !== ''));

        setHeaders(headerRow);
        setParsedData(dataRows);
        
        const detectedMapping = autoDetectColumns(headerRow);
        const applied = applySavedMapping(headerRow, detectedMapping);
        setColumnMapping(applied.mapping);
        setMappingNotice(applied.notice);
        
        setStep(2);
      } catch (err) {
        alert(FILE_READ_ERROR);
      }
    };

    reader.readAsArrayBuffer(uploadedFile);
  };

  const handlePreview = async () => {
    if (!selectedAccount) {
      alert('Please select an account for these transactions');
      return;
    }

    if (columnMapping.date === '' || columnMapping.description === '' || columnMapping.amount === '') {
      alert('Please map at least Date, Description, and Amount columns');
      return;
    }

    setIsProcessing(true);
    
    try {
      const transformed = [];
      const warningList = [];
      const unmatchedCats = [];
      let categoryList = [...allCategories];
      const uncategorizedByType = {};

      const ensureUncategorized = async (transactionType) => {
        const type = transactionType || 'expense';
        if (uncategorizedByType[type]) return uncategorizedByType[type];
        const existing = categoryList.find(
          (c) => c.type === type && String(c.name).trim().toLowerCase() === 'uncategorized'
        );
        if (existing) {
          uncategorizedByType[type] = existing;
          return existing;
        }
        const response = await categoriesAPI.create({
          name: 'Uncategorized',
          type,
          color: null,
          icon: null
        });
        categoryList = [...categoryList, response.data];
        uncategorizedByType[type] = response.data;
        return response.data;
      };

      // Process each transaction - AWAIT PROPERLY IN LOOP
      for (let index = 0; index < parsedData.length; index++) {
        const row = parsedData[index];
        const rowNum = index + 2; // +2 because of 0-index and header row
        const date = parseDate(row[columnMapping.date]);
        const description = cleanDescription(row[columnMapping.description]);
        const rawAmount = parseAmount(row[columnMapping.amount]);
        const amount = rawAmount !== null ? rawAmount.replace(/^-/, '') : null;
        // Get bank category if provided
        const bankCategory = columnMapping.category !== '' ? cleanDescription(row[columnMapping.category]) : null;
        
        // Determine type - if bank category matches type keywords, use it for both type and category
        const typeCell = columnMapping.type !== '' ? row[columnMapping.type] : null;
        let type = rowType(typeCell, rawAmount, typeCell);
        let matchedCategory = null;
        let needsCategoryReview = false;
        
        if (bankCategory && bankCategory.trim()) {
          // Check if bank category is actually a transaction type keyword
          if (isBankCategoryATransactionType(bankCategory)) {
            // Use it to set type, don't treat as category
            type = rowType(bankCategory, rawAmount, typeCell);
          } else {
            // Try to match bank category to our existing categories
            try {
              matchedCategory = await matchBankCategory(bankCategory, type);
            } catch (error) {
              console.error('Error matching category:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
            }
            
            if (!matchedCategory) {
              // Owner follow-up: do not block the wizard; map to Uncategorized.
              try {
                matchedCategory = await ensureUncategorized(type);
              } catch (error) {
                console.error('Error creating Uncategorized:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
                needsCategoryReview = true;
                if (!unmatchedCats.find(c => c.name === bankCategory && c.type === type)) {
                  unmatchedCats.push({
                    name: bankCategory,
                    type: type,
                    transactions: []
                  });
                }
                const unmatchedCat = unmatchedCats.find(c => c.name === bankCategory && c.type === type);
                unmatchedCat.transactions.push(rowNum);
              }
            }
          }
        }
        
        const transaction = {
          transaction_date: date,
          description: description,
          amount: amount,
          transaction_type: type,
          direction: rowDirection(rawAmount, typeCell), // bank's in/out only; amount is saved positive
          account_id: selectedAccount,
          category_id: matchedCategory ? matchedCategory.id : null,
          _bankCategory: bankCategory, // Store original bank category
          _matchedCategory: matchedCategory,
          _needsCategoryReview: needsCategoryReview,
          payee: columnMapping.payee !== '' ? cleanDescription(row[columnMapping.payee]) : null,
          reference_number: columnMapping.reference !== '' ? cleanDescription(row[columnMapping.reference]) : null,
          notes: null,
          _rowNumber: rowNum,
          _hasWarnings: false,
          _originalIndex: index // Track original index for category mapping
        };

        // Track warnings for null required fields
        const rowWarnings = [];
        if (!date) rowWarnings.push('Missing/Invalid date');
        if (!description) rowWarnings.push('Missing description');
        if (amount === null || !/[1-9]/.test(amount)) rowWarnings.push('Missing/Invalid amount');
        if (!type) rowWarnings.push('Cannot determine transaction type');
        if (needsCategoryReview) rowWarnings.push(`Category "${bankCategory}" needs review`);

        if (rowWarnings.length > 0) {
          transaction._hasWarnings = true;
          warningList.push({
            row: rowNum,
            warnings: rowWarnings,
            data: transaction
          });
        }

        transformed.push(transaction);
      }

      if (categoryList.length !== allCategories.length) {
        setAllCategories(categoryList);
      }
      // Flag rows already in the account right on the preview (the review
      // step after Continue still lets the person decide on each one).
      try {
        const checkable = transformed.filter((t) => t.transaction_date && t.description && t.amount !== null);
        markDuplicates(checkable, await findDuplicates(selectedAccount, checkable));
      } catch (error) {
        // The check is advisory here; Continue checks again before importing.
      }
      setPreviewData(transformed);
      setWarnings(warningList);
      setUnmatchedCategories(unmatchedCats);
      
      // Initialize category mappings - map each transaction index to null (no category selected yet)
      const initialMappings = {};
      transformed.forEach((t, index) => {
        if (t._needsCategoryReview) {
          initialMappings[index] = null; // Will be set to category ID by user
        }
      });
      setCategoryMappings(initialMappings);
      
      setStep(3);
    } catch (error) {
      console.error('Error processing transactions:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert(FILE_READ_ERROR);
    } finally {
      setIsProcessing(false);
    }
  };

  // The server skips rows already in the account (counted per matching row),
  // so every valid row is sent and nothing is filtered here.
  const handleCheckDuplicates = async () => {
    if (unmatchedCategories.length > 0) {
      setStep(5);
      return;
    }

    const validTransactions = previewData.filter(t => 
      t.transaction_date && t.description && t.amount !== null && t.transaction_type
    );

    if (validTransactions.length === 0) {
      alert('No valid transactions to import');
      return;
    }

    await reviewDuplicatesThenImport(validTransactions);
  };

  // Shows the duplicate review when any row matches one already in the
  // account; otherwise imports straight away. If the check itself fails the
  // import still runs (the server skips exact duplicates on its own).
  const reviewDuplicatesThenImport = async (rows) => {
    let found = [];
    setIsProcessing(true);
    try {
      found = await findDuplicates(selectedAccount, rows);
    } catch (error) {
      // Never import silently when the check could not finish: say so and
      // let the person try again (nothing was imported).
      setIsProcessing(false);
      alert(`The duplicate check could not finish, so nothing was imported. Please try again. (${formatApiError(error, 'Check failed')})`);
      return;
    } finally {
      setIsProcessing(false);
    }
    if (found.length === 0) {
      await importRows(rows);
      return;
    }
    setDuplicateBatch(rows);
    setDuplicates(found);
    setDuplicateDecisions(defaultDecisions(found));
    setStep(4);
  };

  const handleCategoryReviewComplete = async () => {
    try {
      setIsProcessing(true);
      
      // Keys are full previewData indexes (same as the step-5 UI), never the
      // filtered needsReview array index.
      const missingCategories = previewData.filter((t, index) => (
        t._needsCategoryReview && (categoryMappings[index] == null)
      ));

      if (missingCategories.length > 0) {
        alert(`Please assign categories to all ${missingCategories.length} transaction(s) before continuing.`);
        setIsProcessing(false);
        return;
      }

      // Apply category selections to transactions
      previewData.forEach((t, index) => {
        if (categoryMappings[index] !== undefined && categoryMappings[index] !== null) {
          t.category_id = categoryMappings[index];
          t._needsCategoryReview = false; // Mark as reviewed
        }
      });

      setPreviewData([...previewData]); // Trigger re-render
      
      // Continue to duplicate check
      const validTransactions = previewData.filter(t => 
        t.transaction_date && t.description && t.amount !== null && t.transaction_type
      );

      if (validTransactions.length === 0) {
        alert('No valid transactions to import');
        setIsProcessing(false);
        return;
      }

      await reviewDuplicatesThenImport(validTransactions);
    } catch (error) {
      console.error('Failed to process categories:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert(formatApiError(error, 'Failed to process'));
    } finally {
      setIsProcessing(false);
    }
  };

  // Open add category modal with prefilled bank category
  const openAddCategoryModal = (bankCategory = '', transactionType = 'expense') => {
    setCurrentBankCategory(bankCategory);
    setNewCategoryName(bankCategory); // Prefill with bank category
    setNewCategoryType(transactionType); // Set type based on transaction
    setShowAddCategoryModal(true);
  };

  const handleAddNewCategory = async () => {
    try {
      if (!newCategoryName.trim()) {
        alert('Please enter a category name');
        return;
      }

      const response = await categoriesAPI.create({
        name: newCategoryName.trim(),
        type: newCategoryType,
        color: null,
        icon: null
      });

      // Add to categories list - now available in all dropdowns
      setAllCategories([...allCategories, response.data]);
      
      // Reset form
      setNewCategoryName('');
      setNewCategoryType('expense');
      setCurrentBankCategory('');
      setShowAddCategoryModal(false);
      
      alert(`✓ Category "${response.data.name}" created successfully!\n\nThis category is now available in the dropdown and will auto-match in future uploads.`);
    } catch (error) {
      console.error('Failed to create category:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to create category. Please try again.');
    }
  };

  // A ref, not state, so a second click before the re-render is still caught.
  const importInFlight = useRef(false);

  const importRows = async (rows) => {
    if (importInFlight.current) return;
    if (!rows || rows.length === 0) {
      alert('No valid transactions to import');
      return;
    }

    const cleanedTransactions = rows.map(t => {
      const cleaned = {
        transaction_date: t.transaction_date,
        description: t.description,
        amount: t.amount,
        transaction_type: t.transaction_type,
        direction: t.direction || null,
        account_id: t.account_id,
        category_id: t.category_id,
        payee: t.payee,
        reference_number: t.reference_number,
        notes: t.notes
      };
      return cleaned;
    });

    importInFlight.current = true;
    setIsProcessing(true);
    try {
      // The modal stays open with the mapping intact unless the import succeeded.
      const imported = await onImport(cleanedTransactions);
      if (imported === true) handleClose();
    } finally {
      importInFlight.current = false;
      setIsProcessing(false);
    }
  };

  // Step 4: import everything except the rows the person chose to skip.
  const handleImport = async () => {
    await importRows(rowsToImport(duplicateBatch, duplicateDecisions));
  };

  const handleClose = () => {
    // Force reset all states
    try {
      setFile(null);
      setParsedData([]);
      setHeaders([]);
      setColumnMapping({
        date: '',
        description: '',
        amount: '',
        type: '',
        category: '',
        payee: '',
        reference: ''
      });
      setSelectedAccount('');
      setPreviewData([]);
      setWarnings([]);
      setShowWarningConfirm(false);
      setDuplicates([]);
      setDuplicateDecisions({});
      setDuplicateBatch([]);
      setChargeSign('negative');
      setChargeSignTouched(false);
      setPaymentsAsTransfers(true);
      setUnmatchedCategories([]);
      setCategoryMappings({});
      setIsProcessing(false);
      setMappingNotice('');
      setStep(1);
    } catch (error) {
      console.error('Error resetting modal state:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
    } finally {
      // Always call onClose even if there's an error
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        // Close modal if clicking the backdrop
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <ErrorBoundary>
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-2xl font-bold text-gray-900">Import Transactions from Excel</h2>
            <button 
              onClick={handleClose} 
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close modal"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </ErrorBoundary>

        {/* Progress Steps */}
        <ErrorBoundary>
          <div className="flex items-center justify-center p-4 bg-gray-50 border-b overflow-x-auto">
            <div className="flex items-center space-x-2">
              <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                  1
                </div>
                <span className="ml-2 font-medium text-sm">Upload</span>
              </div>
              <div className="w-12 h-0.5 bg-gray-300"></div>
              <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                  2
                </div>
                <span className="ml-2 font-medium text-sm">Map</span>
              </div>
              <div className="w-12 h-0.5 bg-gray-300"></div>
              <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                  3
                </div>
                <span className="ml-2 font-medium text-sm">Preview</span>
              </div>
              {unmatchedCategories.length > 0 && (
                <>
                  <div className="w-12 h-0.5 bg-gray-300"></div>
                  <div className={`flex items-center ${step >= 5 ? 'text-orange-600' : 'text-gray-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 5 ? 'bg-orange-600 text-white' : 'bg-gray-200'}`}>
                      4
                    </div>
                    <span className="ml-2 font-medium text-sm">Categories</span>
                  </div>
                </>
              )}
              {duplicates.length > 0 && (
                <>
                  <div className="w-12 h-0.5 bg-gray-300"></div>
                  <div className={`flex items-center ${step >= 4 ? 'text-blue-600' : 'text-gray-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                      {unmatchedCategories.length > 0 ? '5' : '4'}
                    </div>
                    <span className="ml-2 font-medium text-sm">Duplicates</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </ErrorBoundary>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Upload File */}
          {step === 1 && (
            <ErrorBoundary>
              <div className="max-w-2xl mx-auto">
                {/* Account Selection - FIRST */}
                <ErrorBoundary>
                  <div className="mb-6 bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6">
                    <label className="block text-lg font-semibold text-gray-900 mb-3">
                      Step 1: Select Account <span className="text-red-500">*</span>
                    </label>
                    <p className="text-sm text-gray-600 mb-3">
                      All imported transactions will be assigned to this account
                    </p>
                    <select
                      value={selectedAccount}
                      onChange={(e) => setSelectedAccount(e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">-- Select Account --</option>
                      {accounts.map(account => (
                        <option key={account.id} value={account.id}>
                          {account.account_name} ({account.account_type})
                        </option>
                      ))}
                    </select>
                  </div>
                </ErrorBoundary>

                {/* File Upload */}
                <ErrorBoundary>
                  <div className={`border-2 border-dashed rounded-lg p-12 text-center transition ${
                    selectedAccount 
                      ? 'border-gray-300 hover:border-blue-500 cursor-pointer' 
                      : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                  }`}>
                    <DocumentArrowUpIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Step 2: Upload Excel File
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Supports .xlsx, .xls, and .csv files from any bank
                    </p>
                    {selectedAccount ? (
                      <>
                        <input
                          type="file"
                          accept=".xlsx,.xls,.csv"
                          onChange={handleFileUpload}
                          className="hidden"
                          id="file-upload"
                        />
                        <label
                          htmlFor="file-upload"
                          className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 cursor-pointer transition"
                        >
                          Choose File
                        </label>
                      </>
                    ) : (
                      <div className="inline-block bg-gray-400 text-white px-6 py-3 rounded-lg cursor-not-allowed">
                        Select Account First
                      </div>
                    )}
                    {file && (
                      <p className="mt-4 text-sm text-gray-600">
                        Selected: <span className="font-medium">{file.name}</span>
                      </p>
                    )}
                  </div>
                </ErrorBoundary>

                <ErrorBoundary>
                  <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Tips:</h4>
                    <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                      <li>File should have headers in the first row</li>
                      <li>Required columns: Date, Description, Amount</li>
                      <li>Optional columns: Type, Category, Payee, Reference</li>
                      <li>Transactions with missing required fields will be flagged for review</li>
                    </ul>
                  </div>
                </ErrorBoundary>

                <ErrorBoundary>
                  <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="font-medium text-purple-900 mb-2">Transaction Types:</h4>
                    <ul className="text-sm text-purple-800 space-y-1">
                      <li><span className="font-semibold">Income:</span> Money received (positive amounts) - salary, payments received</li>
                      <li><span className="font-semibold">Expense:</span> Money spent (negative amounts) - bills, purchases</li>
                      <li><span className="font-semibold">Refund:</span> Money returned (positive amounts) - returns, reimbursements</li>
                      <li><span className="font-semibold">Transfer:</span> Moving money between accounts (neutral)</li>
                    </ul>
                    <p className="text-xs text-purple-700 mt-2">
                      💡 If no Type column is specified, positive amounts = income, negative = expense
                    </p>
                  </div>
                </ErrorBoundary>
              </div>
            </ErrorBoundary>
          )}

          {/* Step 2: Map Columns */}
          {step === 2 && (
            <ErrorBoundary>
              <div>
                <div className="flex items-center justify-between mb-4 gap-3">
                  <h3 className="text-lg font-semibold">Map Excel Columns to Transaction Fields</h3>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={saveColumnMapping}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                    >
                      Save mapping for next time
                    </button>
                    <button
                      type="button"
                      onClick={resetColumnMapping}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition text-sm font-medium"
                    >
                      Reset mapping
                    </button>
                  </div>
                </div>
                {mappingNotice && (
                  <p className="mb-4 text-sm text-green-700">{mappingNotice}</p>
                )}

                {liabilityImport && (
                  <ErrorBoundary>
                    <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2" data-testid="liability-import-options">
                      <p className="text-sm font-medium text-amber-900">This is a card, credit line or loan. Check how this file shows amounts:</p>
                      <label className="block text-sm text-amber-900">In this file, charges (money spent on this account) are
                        <select value={chargeSign} onChange={(e) => { setChargeSign(e.target.value); setChargeSignTouched(true); }}
                          className="ml-2 border border-amber-300 rounded px-2 py-1" aria-label="Charge sign">
                          <option value="negative">negative amounts (for example -25.00)</option>
                          <option value="positive">positive amounts (for example 25.00)</option>
                        </select>
                      </label>
                      <p className="text-xs text-amber-800">We picked the sign most rows use. Charges are saved as expenses; payments and credits lower what you owe.</p>
                      <label className="flex items-center gap-2 text-sm text-amber-900">
                        <input type="checkbox" checked={paymentsAsTransfers} onChange={(e) => setPaymentsAsTransfers(e.target.checked)} />
                        Save payments to this account as transfers (not income)
                      </label>
                    </div>
                  </ErrorBoundary>
                )}
                
                {/* Show selected account */}
                <ErrorBoundary>
                  <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="text-sm font-medium text-green-800">
                      Importing to Account: <span className="font-bold">
                        {accounts.find(a => a.id === parseInt(selectedAccount))?.account_name}
                      </span>
                    </div>
                  </div>
                </ErrorBoundary>

                <div className="grid grid-cols-2 gap-4">
                  {/* Required Mappings */}
                  <ErrorBoundary>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date Column <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={columnMapping.date}
                        onChange={(e) => setColumnMapping({...columnMapping, date: e.target.value})}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      >
                        <option value="">-- Select Column --</option>
                        {headers.map((header, index) => (
                          <option key={index} value={index}>{header || `Column ${index + 1}`}</option>
                        ))}
                      </select>
                    </div>
                  </ErrorBoundary>

                  <ErrorBoundary>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description Column <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={columnMapping.description}
                        onChange={(e) => setColumnMapping({...columnMapping, description: e.target.value})}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      >
                        <option value="">-- Select Column --</option>
                        {headers.map((header, index) => (
                          <option key={index} value={index}>{header || `Column ${index + 1}`}</option>
                        ))}
                      </select>
                    </div>
                  </ErrorBoundary>

                  <ErrorBoundary>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Amount Column <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={columnMapping.amount}
                        onChange={(e) => setColumnMapping({...columnMapping, amount: e.target.value})}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      >
                        <option value="">-- Select Column --</option>
                        {headers.map((header, index) => (
                          <option key={index} value={index}>{header || `Column ${index + 1}`}</option>
                        ))}
                      </select>
                    </div>
                  </ErrorBoundary>

                  {/* Optional Mappings */}
                  <ErrorBoundary>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Type Column (Optional)
                      </label>
                      <select
                        value={columnMapping.type}
                        onChange={(e) => setColumnMapping({...columnMapping, type: e.target.value})}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      >
                        <option value="">-- Auto-detect from amount --</option>
                        {headers.map((header, index) => (
                          <option key={index} value={index}>{header || `Column ${index + 1}`}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Supported types: <span className="font-medium">income</span>, <span className="font-medium">expense</span>, <span className="font-medium">refund</span>, <span className="font-medium">transfer</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {liabilityImport
                        ? '💡 Auto-detect for cards and loans: charges = expense, payments = transfer (see the box above)'
                        : '💡 Auto-detect: Positive = income, Negative = expense'}
                      </p>
                    </div>
                  </ErrorBoundary>

                  <ErrorBoundary>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category Column (Optional)
                      </label>
                      <select
                        value={columnMapping.category}
                        onChange={(e) => setColumnMapping({...columnMapping, category: e.target.value})}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      >
                        <option value="">-- None --</option>
                        {headers.map((header, index) => (
                          <option key={index} value={index}>{header || `Column ${index + 1}`}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Bank's category name - we'll try to match it to your existing categories
                      </p>
                    </div>
                  </ErrorBoundary>

                  <ErrorBoundary>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payee Column (Optional)
                      </label>
                      <select
                        value={columnMapping.payee}
                        onChange={(e) => setColumnMapping({...columnMapping, payee: e.target.value})}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      >
                        <option value="">-- None --</option>
                        {headers.map((header, index) => (
                          <option key={index} value={index}>{header || `Column ${index + 1}`}</option>
                        ))}
                      </select>
                    </div>
                  </ErrorBoundary>

                  <ErrorBoundary>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reference Column (Optional)
                      </label>
                      <select
                        value={columnMapping.reference}
                        onChange={(e) => setColumnMapping({...columnMapping, reference: e.target.value})}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      >
                        <option value="">-- None --</option>
                        {headers.map((header, index) => (
                          <option key={index} value={index}>{header || `Column ${index + 1}`}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Bank transaction reference, check number, or confirmation code for matching
                      </p>
                    </div>
                  </ErrorBoundary>
                </div>

                {/* Sample Data Preview */}
                <ErrorBoundary>
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Sample Data (First 3 rows):</h4>
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            {headers.map((header, index) => (
                              <th key={index} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                {header || `Column ${index + 1}`}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {parsedData.slice(0, 3).map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              {row.map((cell, cellIndex) => (
                                <td key={cellIndex} className="px-4 py-2 text-sm text-gray-900">
                                  {String(cell).substring(0, 50)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </ErrorBoundary>
              </div>
            </ErrorBoundary>
          )}

          {/* Step 3: Preview & Import */}
          {step === 3 && (
            <ErrorBoundary>
              <div>
                <h3 className="text-lg font-semibold mb-4">Preview Transactions</h3>

                {/* Category Matching Info */}
                {unmatchedCategories.length > 0 && (
                  <ErrorBoundary>
                    <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-blue-900 mb-2">Category Matching Results:</h4>
                      <div className="text-sm text-blue-800 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            ✓ Matched
                          </span>
                          <span>Categories automatically matched to existing categories in your system</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                            ⚠ Needs Review
                          </span>
                          <span>{unmatchedCategories.length} bank categor{unmatchedCategories.length === 1 ? 'y' : 'ies'} didn't match - you'll review these in the next step</span>
                        </div>
                      </div>
                    </div>
                  </ErrorBoundary>
                )}
                
                {/* Warning Banner */}
                {warnings.length > 0 && (
                  <ErrorBoundary>
                    <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4">
                      <div className="flex">
                        <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400 mr-3" />
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-yellow-800">
                            {warnings.length} transaction(s) have warnings
                          </h4>
                          <p className="text-sm text-yellow-700 mt-1">
                            These transactions are missing required fields and may not import correctly.
                            You can continue with the import and edit these transactions later, or cancel and fix your Excel file.
                          </p>
                          <details className="mt-2">
                            <summary className="text-sm font-medium text-yellow-800 cursor-pointer hover:underline">
                              View warnings ({warnings.length})
                            </summary>
                            <ul className="mt-2 text-sm text-yellow-700 space-y-1 ml-4 list-disc">
                              {warnings.slice(0, 10).map((warning, index) => (
                                <li key={index}>
                                  Row {warning.row}: {warning.warnings.join(', ')}
                                </li>
                              ))}
                              {warnings.length > 10 && (
                                <li className="text-yellow-600">...and {warnings.length - 10} more</li>
                              )}
                            </ul>
                          </details>
                        </div>
                      </div>
                    </div>
                  </ErrorBoundary>
                )}

                {/* Summary Stats */}
                <ErrorBoundary>
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-sm text-blue-600 font-medium">Total Transactions</div>
                      <div className="text-2xl font-bold text-blue-900">{previewData.length}</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-sm text-green-600 font-medium">Valid (new)</div>
                      <div className="text-2xl font-bold text-green-900">
                        {previewData.filter(t => !t._hasWarnings && !t._duplicate).length}
                      </div>
                      {duplicateCounts(previewData).total > 0 && (
                        <div className="text-xs text-orange-700 mt-1" data-testid="preview-duplicates">
                          {duplicateCounts(previewData).exact} already imported, {duplicateCounts(previewData).possible} possible duplicate(s). You decide on the next step.
                        </div>
                      )}
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <div className="text-sm text-yellow-600 font-medium">With Warnings</div>
                      <div className="text-2xl font-bold text-yellow-900">{warnings.length}</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="text-sm text-purple-600 font-medium">Categories Matched</div>
                      <div className="text-2xl font-bold text-purple-900">
                        {previewData.filter(t => t._matchedCategory).length}
                      </div>
                      {unmatchedCategories.length > 0 && (
                        <div className="text-xs text-orange-600 mt-1">
                          {unmatchedCategories.length} need review
                        </div>
                      )}
                    </div>
                  </div>
                </ErrorBoundary>

                {/* Preview Table */}
                <ErrorBoundary>
                  <div className="overflow-x-auto border rounded-lg max-h-96 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {previewData.slice(0, 50).map((transaction, index) => (
                          <tr key={index} className={transaction._hasWarnings ? 'bg-yellow-50' : ''}>
                            <ErrorBoundary>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {transaction.transaction_date || <span className="text-red-500">Missing</span>}
                              </td>
                            </ErrorBoundary>
                            <ErrorBoundary>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {transaction.description || <span className="text-red-500">Missing</span>}
                              </td>
                            </ErrorBoundary>
                            <ErrorBoundary>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {transaction.amount !== null ? `$${transaction.amount}` : <span className="text-red-500">Missing</span>}
                              </td>
                            </ErrorBoundary>
                            <ErrorBoundary>
                              <td className="px-4 py-2 text-sm">
                                {transaction.transaction_type ? (
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColorClass(transaction.transaction_type)}`}>
                                    {transaction.transaction_type}
                                  </span>
                                ) : <span className="text-red-500">Unknown</span>}
                              </td>
                            </ErrorBoundary>
                            <ErrorBoundary>
                              <td className="px-4 py-2 text-sm">
                                {transaction._matchedCategory ? (
                                  <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                                    ✓ {transaction._matchedCategory.name}
                                  </span>
                                ) : transaction._bankCategory ? (
                                  <span className="px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                    ⚠ {transaction._bankCategory}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-xs">Not set</span>
                                )}
                              </td>
                            </ErrorBoundary>
                            <ErrorBoundary>
                              <td className="px-4 py-2 text-sm">
                                {transaction.reference_number ? (
                                  <span className="text-xs font-mono bg-blue-50 px-2 py-1 rounded text-blue-700">
                                    {transaction.reference_number}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-xs">—</span>
                                )}
                              </td>
                            </ErrorBoundary>
                            <ErrorBoundary>
                              <td className="px-4 py-2 text-sm">
                                {transaction._duplicate === 'exact' ? (
                                  <span className="text-orange-700 text-xs">Already imported</span>
                                ) : transaction._duplicate === 'possible' ? (
                                  <span className="text-orange-600 text-xs">Possible duplicate</span>
                                ) : transaction._hasWarnings ? (
                                  <span className="text-yellow-600 text-xs">⚠ Warning</span>
                                ) : (
                                  <span className="text-green-600 text-xs">✓ Valid</span>
                                )}
                              </td>
                            </ErrorBoundary>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {previewData.length > 50 && (
                      <div className="bg-gray-50 p-3 text-center text-sm text-gray-600">
                        Showing first 50 of {previewData.length} transactions
                      </div>
                    )}
                  </div>
                </ErrorBoundary>
              </div>
            </ErrorBoundary>
          )}

          {/* Step 4: Duplicate Review */}
          {step === 4 && (
            <ErrorBoundary>
              <div>
                <h3 className="text-lg font-semibold mb-4">Review Potential Duplicates</h3>
                
                <ErrorBoundary>
                  <div className="mb-6 bg-orange-50 border-l-4 border-orange-400 p-4">
                    <div className="flex">
                      <ExclamationTriangleIcon className="h-6 w-6 text-orange-400 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="text-sm font-medium text-orange-800">
                          {duplicates.length} potential duplicate(s) found
                        </h4>
                        <p className="text-sm text-orange-700 mt-1">
                          These rows look like transactions already in this account. <strong>Already imported</strong> rows (same date, amount and description, or the same bank reference) are always skipped.
                          <strong> Possible</strong> duplicates (same amount within a few days) are skipped unless you choose <strong>Import</strong>.
                        </p>
                      </div>
                    </div>
                  </div>
                </ErrorBoundary>

                <div className="space-y-6">
                  {duplicates.map((dup, dupIndex) => (
                    <ErrorBoundary key={dupIndex}>
                      <div className="border-2 border-orange-200 rounded-lg p-4 bg-white">
                        <div className="flex items-start justify-between mb-4">
                          <ErrorBoundary>
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 mb-1">
                                Row {duplicateBatch[dup.index]?._rowNumber ?? dup.index + 1} of the file
                              </h4>
                              <p className="text-xs text-gray-500">{dup.match === 'exact' ? 'Already imported: this row will be skipped' : 'Possible duplicate: you decide'}</p>
                            </div>
                          </ErrorBoundary>
                          <ErrorBoundary>
                            {dup.match !== 'exact' && <div className="flex gap-2">
                              <button
                                onClick={() => setDuplicateDecisions({...duplicateDecisions, [dup.index]: 'accept'})}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                                  duplicateDecisions[dup.index] === 'accept'
                                    ? 'bg-green-600 text-white'
                                    : 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
                                }`}
                              >
                                ✓ Import anyway
                              </button>
                              <button
                                onClick={() => setDuplicateDecisions({...duplicateDecisions, [dup.index]: 'ignore'})}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                                  duplicateDecisions[dup.index] === 'ignore'
                                    ? 'bg-red-600 text-white'
                                    : 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
                                }`}
                              >
                                ✗ Skip
                              </button>
                            </div>}
                          </ErrorBoundary>
                        </div>

                        {/* Account information banner */}
                        <ErrorBoundary>
                          {dup.existingTransactions && dup.existingTransactions.length > 0 && (
                            <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                              <p className="text-sm text-yellow-800">
                                <span className="font-semibold">Found in Account(s):</span>{' '}
                                {Array.from(new Set(dup.existingTransactions.map(t => {
                                  const accountInfo = t.account_name || 'Unknown Account';
                                  const institution = t.institution ? ` (${t.institution})` : '';
                                  return `${accountInfo}${institution}`;
                                }))).join(', ')}
                              </p>
                              <p className="text-xs text-yellow-700 mt-1">
                                {dup.existingTransactions.some(t => t.account_id !== selectedAccount) 
                                  ? '⚠️ Warning: Some duplicates are in different accounts. Verify you are uploading to the correct account.'
                                  : '✓ All duplicates are in the same account you selected.'}
                              </p>
                            </div>
                          )}
                        </ErrorBoundary>

                        <div className="grid grid-cols-2 gap-4">
                          {/* New Transaction */}
                          <ErrorBoundary>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                              <h5 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                                <DocumentArrowUpIcon className="h-4 w-4 mr-1" />
                                NEW (from Excel)
                              </h5>
                              <div className="space-y-2 text-sm">
                                <div>
                                  <span className="text-gray-600">Date:</span>
                                  <span className="ml-2 font-medium text-gray-900">
                                    {formatCalendarDay(dup.newTransaction.transaction_date)}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Description:</span>
                                  <span className="ml-2 font-medium text-gray-900">{dup.newTransaction.description}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Amount:</span>
                                  <span className="ml-2 font-bold text-gray-900">${dup.newTransaction.amount}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Type:</span>
                                  <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${getTypeColorClass(dup.newTransaction.transaction_type)}`}>
                                    {dup.newTransaction.transaction_type}
                                  </span>
                                </div>
                                {dup.newTransaction.reference_number && (
                                  <div>
                                    <span className="text-gray-600">Reference:</span>
                                    <span className="ml-2 text-xs font-mono bg-blue-100 px-2 py-1 rounded text-blue-700">
                                      {dup.newTransaction.reference_number}
                                    </span>
                                  </div>
                                )}
                                {dup.newTransaction.payee && (
                                  <div>
                                    <span className="text-gray-600">Payee:</span>
                                    <span className="ml-2 text-gray-900">{dup.newTransaction.payee}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </ErrorBoundary>

                          {/* Existing Transactions */}
                          <ErrorBoundary>
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                              <h5 className="text-sm font-semibold text-gray-900 mb-3">
                                EXISTING (in database) - {dup.existingTransactions.length} match(es)
                              </h5>
                              <div className="space-y-3 max-h-48 overflow-y-auto">
                                {dup.existingTransactions.map((existing, idx) => (
                                  <ErrorBoundary key={idx}>
                                    <div className="bg-white border border-gray-200 rounded p-3 text-sm">
                                      <div className="space-y-1">
                                        <ErrorBoundary>
                                          <div className="mb-2 pb-2 border-b border-gray-200">
                                            <span className="text-xs font-semibold text-indigo-700">
                                              Account: {existing.account_name || 'Unknown'}
                                            </span>
                                            {existing.institution && (
                                              <span className="text-xs text-gray-500 ml-2">
                                                ({existing.institution})
                                              </span>
                                            )}
                                            {existing.account_type && (
                                              <span className="text-xs text-gray-500 ml-2">
                                                • {existing.account_type}
                                              </span>
                                            )}
                                          </div>
                                        </ErrorBoundary>
                                        <ErrorBoundary>
                                          <div>
                                            <span className="text-gray-600">Date:</span>
                                            <span className="ml-2 font-medium text-gray-900">
                                              {formatCalendarDay(existing.transaction_date)}
                                            </span>
                                          </div>
                                        </ErrorBoundary>
                                        <ErrorBoundary>
                                          <div>
                                            <span className="text-gray-600">Description:</span>
                                            <span className="ml-2 text-gray-900">{existing.description}</span>
                                          </div>
                                        </ErrorBoundary>
                                        <ErrorBoundary>
                                          <div>
                                            <span className="text-gray-600">Amount:</span>
                                            <span className="ml-2 font-bold text-gray-900">${existing.amount}</span>
                                          </div>
                                        </ErrorBoundary>
                                        <ErrorBoundary>
                                          <div>
                                            <span className="text-gray-600">Type:</span>
                                            <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${getTypeColorClass(existing.transaction_type)}`}>
                                              {existing.transaction_type}
                                            </span>
                                          </div>
                                        </ErrorBoundary>
                                        {existing.reference_number && (
                                          <ErrorBoundary>
                                            <div>
                                              <span className="text-gray-600">Reference:</span>
                                              <span className="ml-2 text-xs font-mono bg-gray-100 px-2 py-1 rounded text-gray-700">
                                                {existing.reference_number}
                                              </span>
                                            </div>
                                          </ErrorBoundary>
                                        )}
                                        <ErrorBoundary>
                                          <div className="text-xs text-gray-500 mt-2">
                                            Created: {new Date(existing.created_at).toLocaleString()}
                                          </div>
                                        </ErrorBoundary>
                                      </div>
                                    </div>
                                  </ErrorBoundary>
                                ))}
                              </div>
                            </div>
                          </ErrorBoundary>
                        </div>
                      </div>
                    </ErrorBoundary>
                  ))}
                </div>

                <ErrorBoundary>
                  <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Summary:</h4>
                    <div className="text-sm text-blue-800 space-y-1">
                      <div>Total potential duplicates: <strong>{duplicates.length}</strong></div>
                      <div>
                        Will import: <strong className="text-green-700">
                          {Object.values(duplicateDecisions).filter(d => d === 'accept').length}
                        </strong>
                      </div>
                      <div>
                        Will skip: <strong className="text-red-700">
                          {Object.values(duplicateDecisions).filter(d => d === 'ignore').length}
                        </strong>
                      </div>
                      <div>
                        Other transactions (not duplicates): <strong>
                          {duplicateBatch.length - duplicates.length}
                        </strong>
                      </div>
                    </div>
                  </div>
                </ErrorBoundary>
              </div>
            </ErrorBoundary>
          )}

          {/* Step 5: Category Review */}
          {step === 5 && (
            <ErrorBoundary>
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Assign Categories to Transactions</h3>
                  <ErrorBoundary>
                    <button
                      onClick={() => openAddCategoryModal('', 'expense')}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
                    >
                      + Add New Category
                    </button>
                  </ErrorBoundary>
                </div>
                
                <ErrorBoundary>
                  <div className="mb-6 bg-orange-50 border-l-4 border-orange-400 p-4">
                    <div className="flex">
                      <ExclamationTriangleIcon className="h-6 w-6 text-orange-400 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="text-sm font-medium text-orange-800">
                          {previewData.filter(t => t._needsCategoryReview).length} transaction(s) need category assignment
                        </h4>
                        <p className="text-sm text-orange-700 mt-1">
                          These bank categories couldn't be matched automatically. Select a category for each transaction from the dropdown.
                        </p>
                        <p className="text-sm text-orange-700 mt-2">
                          💡 <strong>Example:</strong> If your bank says "Shopping" → select "Office Supplies" or "Groceries" from dropdown
                        </p>
                        <div className="mt-3 pt-3 border-t border-orange-200">
                          <p className="text-sm font-semibold text-orange-800">
                            Don't see the category you need?
                          </p>
                          <p className="text-sm text-orange-700 mt-1">
                            Click "+ Add New Category" to create it. Once created:
                          </p>
                          <ul className="text-sm text-orange-700 mt-1 ml-4 list-disc">
                            <li>Available immediately in all dropdowns</li>
                            <li>Saved permanently to your Categories table</li>
                            <li>Will auto-match in future imports</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </ErrorBoundary>

                {/* Add Category Modal */}
                {showAddCategoryModal && (
                  <ErrorBoundary>
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <ErrorBoundary>
                          <h4 className="text-lg font-semibold mb-2">Create New Category</h4>
                          {currentBankCategory && (
                            <p className="text-sm text-blue-600 mb-4">
                              💡 Prefilled with bank category: "{currentBankCategory}" - edit as needed
                            </p>
                          )}
                        </ErrorBoundary>
                        <ErrorBoundary>
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Category Name <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                                className="w-full border border-gray-300 rounded px-3 py-2"
                                placeholder="e.g., Office Supplies"
                                autoFocus
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Type <span className="text-red-500">*</span>
                              </label>
                              <select
                                value={newCategoryType}
                                onChange={(e) => setNewCategoryType(e.target.value)}
                                className="w-full border border-gray-300 rounded px-3 py-2"
                              >
                                <option value="expense">Expense</option>
                                <option value="income">Income</option>
                                <option value="refund">Refund</option>
                                <option value="transfer">Transfer</option>
                              </select>
                            </div>
                          </div>
                        </ErrorBoundary>
                        <ErrorBoundary>
                          <div className="flex gap-3 mt-6">
                            <button
                              onClick={() => {
                                setShowAddCategoryModal(false);
                                setNewCategoryName('');
                                setNewCategoryType('expense');
                                setCurrentBankCategory('');
                              }}
                              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleAddNewCategory}
                              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                            >
                              Create Category
                            </button>
                          </div>
                        </ErrorBoundary>
                      </div>
                    </div>
                  </ErrorBoundary>
                )}

                {/* Transaction List with Category Selection */}
                <div className="space-y-4">
                  {previewData.map((transaction, index) => {
                    if (!transaction._needsCategoryReview) return null;
                    
                    return (
                      <ErrorBoundary key={index}>
                        <div className="border-2 border-orange-200 rounded-lg p-4 bg-white">
                          <div className="grid grid-cols-2 gap-4">
                            {/* Transaction Details */}
                            <ErrorBoundary>
                              <div className="bg-gray-50 rounded-lg p-3">
                                <h5 className="text-sm font-semibold text-gray-900 mb-2">
                                  Transaction #{transaction._rowNumber}
                                </h5>
                                <div className="space-y-1 text-sm">
                                  <div>
                                    <span className="text-gray-600">Date:</span>
                                    <span className="ml-2 font-medium text-gray-900">
                                      {formatCalendarDay(transaction.transaction_date)}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Description:</span>
                                    <span className="ml-2 text-gray-900">{transaction.description}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Amount:</span>
                                    <span className="ml-2 font-bold text-gray-900">${transaction.amount}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Type:</span>
                                    <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${getTypeColorClass(transaction.transaction_type)}`}>
                                      {transaction.transaction_type}
                                    </span>
                                  </div>
                                  <div className="pt-2 border-t border-gray-200 mt-2">
                                    <span className="text-gray-600">Bank Category:</span>
                                    <span className="ml-2 font-medium text-orange-700">"{transaction._bankCategory}"</span>
                                  </div>
                                </div>
                              </div>
                            </ErrorBoundary>

                            {/* Category Selection */}
                            <ErrorBoundary>
                              <div>
                                <label className="block text-sm font-semibold text-gray-900 mb-2">
                                  Select Category <span className="text-red-500">*</span>
                                </label>
                                <select
                                  value={categoryMappings[index] || ''}
                                  onChange={(e) => setCategoryMappings(
                                    mapSameBankCategory(previewData, categoryMappings, index, e.target.value ? parseInt(e.target.value, 10) : null)
                                  )}
                                  className={`w-full border rounded px-3 py-2 ${
                                    !categoryMappings[index] ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                  }`}
                                >
                                  <option value="">-- Select a category --</option>
                                  {categoriesForTypes(allCategories, [transaction.transaction_type])
                                    .map(cat => (
                                      <option key={cat.id} value={cat.id}>
                                        {cat.name}
                                      </option>
                                    ))
                                  }
                                </select>
                                {!categoryMappings[index] && (
                                  <p className="text-xs text-red-600 mt-1">
                                    Please select a category to continue
                                  </p>
                                )}
                                <p className="text-xs text-gray-500 mt-2">
                                  Showing {transaction.transaction_type} categories. 
                                  Don't see what you need? <button
                                    onClick={() => openAddCategoryModal(transaction._bankCategory || '', transaction.transaction_type)}
                                    className="text-blue-600 hover:underline font-medium"
                                  >
                                    Add new category
                                  </button>
                                </p>
                              </div>
                            </ErrorBoundary>
                          </div>
                        </div>
                      </ErrorBoundary>
                    );
                  })}
                </div>

                <ErrorBoundary>
                  <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Summary:</h4>
                    <div className="text-sm text-blue-800 space-y-1">
                      <div>
                        Total transactions needing categories: <strong>
                          {previewData.filter(t => t._needsCategoryReview).length}
                        </strong>
                      </div>
                      <div>
                        Categories assigned: <strong className="text-green-700">
                          {previewData.filter((t, index) => t._needsCategoryReview && categoryMappings[index] != null).length}
                        </strong>
                      </div>
                      <div>
                        Still need assignment: <strong className="text-red-700">
                          {previewData.filter((t, index) => t._needsCategoryReview && categoryMappings[index] == null).length}
                        </strong>
                      </div>
                      {previewData.some((t, index) => t._needsCategoryReview && categoryMappings[index] == null) && (
                        <div className="text-red-700 font-medium mt-2">
                          ⚠️ Please assign categories to all transactions before continuing
                        </div>
                      )}
                    </div>
                  </div>
                </ErrorBoundary>
              </div>
            </ErrorBoundary>
          )}
        </div>

        {/* Footer */}
        <ErrorBoundary>
          <div className="flex items-center justify-between p-6 border-t bg-gray-50">
            <button
              onClick={handleClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition"
            >
              Cancel
            </button>
            <div className="flex gap-3">
              {step > 1 && (
                <button
                  type="button"
                  onClick={goBackOneStep}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition"
                >
                  Back
                </button>
              )}
              {step === 2 && (
                <button
                  onClick={handlePreview}
                  disabled={isProcessing}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isProcessing ? 'Processing...' : 'Preview Transactions'}
                </button>
              )}
              {step === 3 && (
                <button
                  onClick={handleCheckDuplicates}
                  disabled={isProcessing}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isProcessing ? 'Checking...' : 'Continue'}
                </button>
              )}
              {step === 5 && (
                <button
                  onClick={handleCategoryReviewComplete}
                  disabled={previewData.some((t, index) => t._needsCategoryReview && categoryMappings[index] == null) || isProcessing}
                  className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isProcessing ? 'Processing...' : 'Apply Categories & Continue'}
                </button>
              )}
              {step === 4 && (
                <button
                  onClick={handleImport}
                  disabled={isProcessing}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
                >
                  Import Selected Transactions
                </button>
              )}
            </div>
          </div>
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default ImportTransactionsModal;
